import { Ref } from 'vue';

import { MaybePromise } from '@/_redesign/shared/types/MaybePromise';
import {
  errorNotification as sendErrorNotification,
  successNotification as sendSuccessNotification,
} from '@/_redesign/shared/ui/NotificationPopup/notification-popup';
import { IApiResponse, TErrorResponse, TSuccessResponse } from '@/types/interfaces/ApiResponse.interface';
import { IServerError } from '@/types/interfaces/ServerError.interface';

type PendingFunction = (newValue: boolean) => void | Promise<void>;

export type RequestHandlerPending = Ref<boolean> | PendingFunction;

function makePendingFunction(pending?: RequestHandlerPending) {
  if (typeof pending === 'function' || pending === undefined) return pending;
  return (newValue: boolean) => {
    pending.value = newValue;
  };
}

type NotificationType = string | {
  title: string;
  message: string;
};

export interface FetchHandlerArgs<T> {
  onSuccess?: (res: T, response: TSuccessResponse<T>) => MaybePromise<void>;
  onError?: (err: IServerError, response: TErrorResponse) => MaybePromise<void>;
  onRequest?: () => MaybePromise<void>;
  pending?: RequestHandlerPending;
  preventIfPending?: boolean;
  setPendingAfterHandlers?: boolean;
  delayTime?: number;
  errorNotification?: NotificationType | false;
  successNotification?: NotificationType | true;
}

/**
 * Handles a request and provides success and error handling.
 *
 * @template T The type of the response data
 * @param request - A function that returns a Promise of an API response.
 * @param args - Optional arguments for handling the request:
 *   - `onSuccess`: A callback function to be called on successful response.
 *   - `onError`: A callback function to be called on error response.
 *   - `onRequest`: A callback function to be called after response and before pending being complete.
 *   - `pending`: A ref to a boolean value that will be set to true during the request and false afterward.
 *   - `errorNotification`: A string to be used for the error notification, or `false` to disable it.
 *   - `successNotification`: A string to be used for the success notification, or `true` to use a default message.
 * @returns The API response.
 */
export async function requestHandler<T>(
  request: () => Promise<IApiResponse<T>>,
  args: FetchHandlerArgs<T> = {},
) {
  const {
    onSuccess,
    onError,
    onRequest,

    errorNotification,
    successNotification,

    setPendingAfterHandlers,
  } = args;
  const pending = makePendingFunction(args.pending);

  pending?.(true);
  const response = await request();
  await onRequest?.();
  if (!setPendingAfterHandlers) pending?.(false);

  if (response?.error !== null) {
    await onError?.(response.error, response);
    if (errorNotification !== false) {
      sendErrorNotification(errorNotification || response.error);
    }
    if (setPendingAfterHandlers) pending?.(false);
    return response;
  }

  await onSuccess?.(response.response, response);
  if (successNotification) {
    const successMessage = successNotification === true ? undefined : successNotification;
    sendSuccessNotification(successMessage);
  }
  if (setPendingAfterHandlers) pending?.(false);
  return response;
}

function mergeArgs<T>(first: FetchHandlerArgs<any>, second: FetchHandlerArgs<any>) {
  const firstPending = makePendingFunction(first.pending);
  const secondPending = makePendingFunction(second.pending);

  return {
    errorNotification: second.errorNotification ?? first.errorNotification,
    successNotification: second.successNotification ?? first.successNotification,
    onSuccess: (data: T, response: TSuccessResponse<T>) => {
      first.onSuccess?.(data, response);
      second.onSuccess?.(data, response);
    },
    onError: (error: IServerError, response: TErrorResponse) => {
      first.onError?.(error, response);
      second.onError?.(error, response);
    },
    pending: (newValue: boolean) => {
      firstPending?.(newValue);
      secondPending?.(newValue);
    },
  };
}

/**
 * Creates a request handler function that can be used to make API requests.
 *
 * @template T - The type of the response data.
 * @template D - The type of the request data.
 * @param request - A function that makes the API request and returns a Promise of the API response.
 * @param args - Optional arguments for handling the request:
 *   - `onSuccess`: A callback function to be called on successful response.
 *   - `onError`: A callback function to be called on error response.
 *   - `pending`: A ref to a boolean value that will be set to true during the request and false afterward.
 *   - `errorNotification`: A string to be used for the error notification, or `false` to disable it.
 *   - `successNotification`: A string to be used for the success notification, or `true` to use a default message.
 * @param secondArgs - Same as args.
 * @returns A function that can be called with request data and optional additional arguments to make the API request and handle the response.
 */
export function getRequestHandler<T>(
  request: () => Promise<IApiResponse<T>>,
  args?: FetchHandlerArgs<T>,
): (secondArgs?: FetchHandlerArgs<T>) => Promise<IApiResponse<T>>;

export function getRequestHandler<T, D>(
  request: (data: D) => Promise<IApiResponse<T>>,
  args?: FetchHandlerArgs<T>,
): (data: D, secondArgs?: FetchHandlerArgs<T>) => Promise<IApiResponse<T>>;

export function getRequestHandler<T, D>(
  request: (data?: D) => Promise<IApiResponse<T>>,
  args: FetchHandlerArgs<T> = {},
): (data: D extends undefined ? never : D, secondArgs?: FetchHandlerArgs<T>) => Promise<IApiResponse<T>> {
  if (request.length === 0) {
    return ((secondArgs: FetchHandlerArgs<T> = {}) => requestHandler(
      request,
      mergeArgs<T>(args, secondArgs),
    )) as (data: D extends undefined ? never : D, secondArgs?: FetchHandlerArgs<T>) => Promise<IApiResponse<T>>;
  }
  return ((data: D, secondArgs: FetchHandlerArgs<T> = {}) => requestHandler(
    () => request(data),
    mergeArgs<T>(args, secondArgs),
  )) as (data: D extends undefined ? never : D, secondArgs?: FetchHandlerArgs<T>) => Promise<IApiResponse<T>>;
}
